import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, AuthResponse } from '../types/auth';
import apiService, { setTokenExpiry } from '../services/api';
import toast from 'react-hot-toast';

/**
 * Проверяет, истёк ли токен по сохранённому времени истечения
 */
const isTokenExpired = (): boolean => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return true;
    return Date.now() > parseInt(tokenExpiry, 10);
};

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (credentials: { login: string; password: string }) => Promise<void>;
    logout: () => void;
    updateUser: (userData: Partial<User>) => void;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        // Пытаемся восстановить пользователя из localStorage при инициализации
        try {
            const savedUser = localStorage.getItem('user');
            const token = apiService.getAuthToken();

            // Проверяем, не истёк ли токен
            if (savedUser && token && !isTokenExpired()) {
                const parsedUser = JSON.parse(savedUser);
                console.log('🔄 ВОССТАНОВЛЕНИЕ ПОЛЬЗОВАТЕЛЯ ИЗ localStorage:', {
                    savedUser: parsedUser,
                    hasFrom1C: parsedUser.from1C !== undefined,
                    from1CValue: parsedUser.from1C
                });
                return parsedUser;
            }
            
            // Если токен истёк, очищаем данные
            if (token && isTokenExpired()) {
                console.log('⏰ Токен истёк при загрузке, очищаем данные');
                apiService.removeAuthToken();
                localStorage.removeItem('user');
                localStorage.removeItem('tokenExpiry');
            }
        } catch (error) {
            console.error('Ошибка восстановления пользователя из localStorage:', error);
        }
        return null;
    });

    const [isLoading, setIsLoading] = useState(() => {
        // Если есть токен И нет сохраненного пользователя, начинаем с загрузки
        const token = apiService.getAuthToken();
        const savedUser = localStorage.getItem('user');

        // Загрузка нужна только если есть токен, но нет пользователя и токен не истёк
        const needsLoading = !!token && !savedUser && !isTokenExpired();
        console.log('Инициализация isLoading:', { token: !!token, savedUser: !!savedUser, needsLoading });

        return needsLoading;
    });

    // Функция для автоматического logout при истечении токена
    const handleTokenExpiration = useCallback(() => {
        console.log('⏰ Токен истёк, выполняем автоматический logout');
        apiService.removeAuthToken();
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiry');
        setUser(null);
        toast.error('Сессия истекла. Пожалуйста, войдите снова.');
    }, []);

    useEffect(() => {
        initializeAuth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Периодическая проверка истечения токена (каждую минуту)
    useEffect(() => {
        const checkTokenInterval = setInterval(() => {
            const token = apiService.getAuthToken();
            if (token && user && isTokenExpired()) {
                handleTokenExpiration();
            }
        }, 60000); // Проверяем каждую минуту

        return () => clearInterval(checkTokenInterval);
    }, [user, handleTokenExpiration]);

    // Автоматическое обновление данных пользователя при фокусе на окне
    useEffect(() => {
        const handleFocus = () => {
            // Проверяем токен при возврате на страницу
            if (user && isTokenExpired()) {
                handleTokenExpiration();
                return;
            }
            
            if (user && !isLoading) {
                refreshUser();
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [user, isLoading, handleTokenExpiration]); // eslint-disable-line react-hooks/exhaustive-deps

    const initializeAuth = async () => {
        console.log('=== initializeAuth начало ===');

        // Если уже загрузка не нужна (есть пользователь), просто выходим
        if (!isLoading) {
            console.log('Загрузка не требуется, пользователь уже установлен');
            return;
        }

        try {
            const token = apiService.getAuthToken();
            console.log('Проверяем токен:', !!token);

            if (!token) {
                console.log('Токен отсутствует, очищаем данные');
                localStorage.removeItem('user');
                setUser(null);
                return;
            }

            console.log('Проверяем токен через API...');
            const response = await apiService.getCurrentUser();

            if (response.data && response.data.success && response.data.data) {
                console.log('Токен валиден, обновляем пользователя');
                const userData = response.data.data;
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
            } else {
                console.log('Токен не валиден, очищаем данные');
                apiService.removeAuthToken();
                setUser(null);
            }
        } catch (error: any) {
            console.error('Ошибка проверки токена:', error);

            if (error.response?.status === 401 || error.response?.status === 403) {
                console.log('Токен истёк или нет доступа');
            }

            apiService.removeAuthToken();
            setUser(null);
        } finally {
            setIsLoading(false);
            console.log('=== initializeAuth завершено ===');
        }
    };

    const login = async (credentials: { login: string; password: string }) => {
        try {
            const response = await apiService.login(credentials);
            const authData: AuthResponse = response.data;

            // Сохраняем токены
            localStorage.setItem('accessToken', authData.accessToken);
            localStorage.setItem('refreshToken', authData.refreshToken);
            // Сохраняем время истечения токена (24 часа)
            setTokenExpiry();

            // Устанавливаем пользователя
            const userData: User = {
                id: authData.userId,
                login: authData.login,
                fullName: authData.fullName,
                role: authData.role,
                status: authData.status,
                companyId: authData.companyId,
                from1C: authData.from1C,
                isEmailVerified: false, // Будет обновлено при загрузке полных данных
                isPhoneVerified: false
            };

            console.log('💾 СОХРАНЕНИЕ ПОЛЬЗОВАТЕЛЯ В localStorage:', {
                userData,
                hasFrom1C: userData.from1C !== undefined,
                from1CValue: userData.from1C
            });
            setUser(userData);
            // Сохраняем в localStorage для быстрого восстановления при рефреше
            localStorage.setItem('user', JSON.stringify(userData));
            toast.success('Вход выполнен успешно');
        } catch (error: any) {
            console.error('Ошибка входа:', error);
            const message = error.response?.data?.message || 'Ошибка входа в систему';
            toast.error(message);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await apiService.logout();
        } catch (error) {
            console.error('Ошибка выхода:', error);
        } finally {
            apiService.removeAuthToken();
            localStorage.removeItem('user');
            localStorage.removeItem('tokenExpiry');
            setUser(null);
            toast.success('Вы вышли из системы');
        }
    };

    const refreshUser = async () => {
        try {
            const response = await apiService.getCurrentUser();
            if (response.data && response.data.success && response.data.data) {
                const userData = response.data.data;
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
            }
        } catch (error) {
            console.error('Ошибка обновления данных пользователя:', error);
        }
    };

    const updateUser = (userData: Partial<User>) => {
        console.log('AuthContext: updateUser вызван с данными:', userData);

        if (!user) {
            // Если пользователя нет, создаём нового (для случая админ-входа)
            console.log('AuthContext: создаём нового пользователя из переданных данных');
            const newUser = userData as User;
            setUser(newUser);
            localStorage.setItem('user', JSON.stringify(newUser));
            return;
        }

        // Всегда мержим данные с текущим пользователем, чтобы не потерять важные поля
        console.log('AuthContext: обновляем существующего пользователя');
        const updatedUser: User = { ...user, ...userData };
        setUser(updatedUser);

        // Сохраняем обновленного пользователя в localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser));
    };

    const isAuthenticated = !!user;

    console.log('AuthContext render: user =', user, 'isLoading =', isLoading, 'isAuthenticated =', isAuthenticated);

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated,
        login,
        logout,
        updateUser,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
